import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotes } from '../hooks/useNotes';
import { PwaInstallCard } from '../components/home/PwaInstallCard';
import { DeskCalendar } from '../components/home/DeskCalendar';
import { StatTabs } from '../components/home/StatTabs';
import { AskSticky } from '../components/home/AskSticky';
import { NoteCard } from '../components/NoteCard';
import { Doodle, DoodleKind, EnterOnce, Highlight, Loop } from '../components/paper';
import { deskLine, deskTabs, filterInbox, firstName, greeting, InboxFilter, Scene } from '../lib/desk';

/** The little desk scene in the masthead's corner, by time of day. */
const SCENE_DOODLE: Record<Scene, DoodleKind> = {
  morning: 'coffee',
  afternoon: 'sun',
  evening: 'lamp',
  night: 'moon',
};

/**
 * Home — the web twin of mobile's desk (mobile/app/(app)/(tabs)/index.tsx):
 * calendar · greeting · doodle masthead, highlighter stat tabs, the ask sticky,
 * and the inbox of pages waiting to be filed.
 */
export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { notes, isLoading } = useNotes();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<InboxFilter>('all');

  const now = new Date();
  const hello = greeting(now.getHours());
  const inbox = notes.filter((n) => n.inboxed);
  const tabs = deskTabs(notes, now);
  // A filter whose tab has emptied out falls back to all.
  const filter = picked !== 'all' && tabs[picked] === 0 ? 'all' : picked;
  const shown = filterInbox(inbox, filter, now);
  const toggleToday = (): void => setPicked(filter === 'today' ? 'all' : 'today');

  const ask = (query: string): void => {
    navigate('/workspace', { state: { initialQuery: query } });
  };

  return (
    <div className="mx-auto max-w-[60rem] px-5 pb-16 pt-[calc(1.5rem+var(--mobile-nav-offset))] md:px-10 md:pt-14">
      <header className="flex items-center gap-4 md:gap-6">
        <DeskCalendar date={now} active={filter === 'today'} onClick={tabs.today > 0 ? toggleToday : undefined} />
        <div className="min-w-0 flex-1">
          <p className="pb-eyebrow m-0 truncate">{hello.text},</p>
          <h1 className="pb-heading-1 m-0 mt-1 truncate pr-2">
            <Highlight>{firstName(user?.name ?? user?.email?.split('@')[0])}.</Highlight>
          </h1>
        </div>
        <Doodle kind={SCENE_DOODLE[hello.scene]} size={0.56} className="-mr-3 max-sm:hidden" />
      </header>

      {!isLoading &&
        (tabs.today + tabs.reading + tabs.thisWeek > 0 ? (
          <div className="mt-5">
            <StatTabs {...tabs} filter={filter} onFilter={setPicked} />
          </div>
        ) : (
          <p className="m-0 mt-4 font-body text-sm text-on-surface-variant">{deskLine(0, 0)}</p>
        ))}

      <div className="max-w-[40rem]">
        <AskSticky onAsk={ask} />
      </div>

      <div className="mt-10">
        <PwaInstallCard />
      </div>

      <div className="mb-4 mt-2 flex items-center gap-2">
        <h2 className="pb-eyebrow m-0 not-italic">Inbox</h2>
        {shown.length > 0 && (
          <span className="relative inline-flex h-6 min-w-[30px] items-center justify-center font-body text-xs font-bold text-accent">
            {shown.length}
            <Loop width={30} height={24} seed={shown.length} className="absolute inset-0" />
          </span>
        )}
        <Link to="/library" className="ml-auto font-body text-xs font-semibold text-accent no-underline hover:underline">
          Open library
        </Link>
      </div>

      {shown.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {shown.map((note, i) => (
            <EnterOnce key={note.id} id={`note:${note.id}`} index={i}>
              <NoteCard note={note} />
            </EnterOnce>
          ))}
        </div>
      ) : (
        !isLoading && (
          <div className="flex flex-col items-center gap-2 px-4 pt-6 text-center">
            <Doodle kind="page" />
            <p className="m-0 mt-3 font-headline text-[1.3125rem] italic text-on-surface">
              {notes.length > 0 ? 'Inbox zero. Everything’s filed.' : 'A blank page, full of promise.'}
            </p>
            <p className="m-0 max-w-sm font-body text-sm text-on-surface-variant">
              {notes.length > 0 ? (
                <>
                  Your pages live in the{' '}
                  <Link to="/library" className="text-accent">
                    library
                  </Link>
                  .
                </>
              ) : (
                <>
                  <Link to="/scanner" className="text-accent">
                    Scan your first note
                  </Link>{' '}
                  — we&apos;ll read it, title it, and tag it for you.
                </>
              )}
            </p>
          </div>
        )
      )}
    </div>
  );
};
